"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { createPortal } from "react-dom";

import { cn } from "./index";

const Popover = PopoverPrimitive.Root;

// Context to provide access to the trigger element
const TriggerContext = React.createContext<Element | null>(null);

// Modified PopoverTrigger that captures its DOM element reference
const TriggerWithRef = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>((props, forwardedRef) => {
  const [trigger, setTrigger] = React.useState<Element | null>(null);

  // Create a ref callback to capture the element and forward the ref
  const setRef = React.useCallback(
    (element: React.ElementRef<typeof PopoverPrimitive.Trigger> | null) => {
      setTrigger(element);

      // Handle the forwarded ref
      if (typeof forwardedRef === "function") {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    },
    [forwardedRef],
  );

  return (
    <TriggerContext.Provider value={trigger}>
      <PopoverPrimitive.Trigger {...props} ref={setRef} />
    </TriggerContext.Provider>
  );
});
TriggerWithRef.displayName = "PopoverTrigger";

const PopoverAnchor = PopoverPrimitive.Anchor;

// Simplified portal implementation with containerRoot support
const PopoverPortal = ({
  children,
  containerRoot,
}: {
  children: React.ReactNode;
  containerRoot?: ShadowRoot | Element;
}) => {
  const [mounted, setMounted] = React.useState(false);
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);
  const portalId = React.useId();

  React.useEffect(() => {
    setMounted(true);

    // Create portal container
    const div = document.createElement("div");
    div.setAttribute("data-popover-portal", portalId);
    div.setAttribute("data-fluid-popover-container", "true");

    // If containerRoot is provided, use it directly
    if (containerRoot) {
      // Find the shadow-container inside the containerRoot if it's a ShadowRoot
      if (containerRoot instanceof ShadowRoot) {
        const shadowContainer =
          containerRoot.querySelector(".shadow-container");
        if (shadowContainer) {
          // Append to the shadow-container when possible for better style inheritance
          shadowContainer.appendChild(div);
        } else {
          // Fallback to appending directly to the shadow root
          containerRoot.appendChild(div);
        }
      } else {
        // For non-ShadowRoot elements, append directly
        containerRoot.appendChild(div);
      }
    } else {
      // If no containerRoot provided, use document.body as fallback
      document.body.appendChild(div);
    }

    setPortalContainer(div);

    // Cleanup function
    return () => {
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    };
  }, [portalId, containerRoot]);

  // Only render when we have a container ready
  if (!mounted || !portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer);
};

interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  containerRoot?: ShadowRoot | Element;
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    { className, align = "center", sideOffset = 4, containerRoot, ...props },
    ref,
  ) => (
    <PopoverPortal containerRoot={containerRoot}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "fluid z-30 w-72 origin-[--radix-popover-content-transform-origin] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        {...props}
      />
    </PopoverPortal>
  ),
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export {
  Popover,
  TriggerWithRef as PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  type PopoverContentProps,
};
